import type { Message } from '@copilotkit/shared';

// Module-level id (mirrors WELCOME_MESSAGE_ID in useCopilotKitAgent.ts) shared
// by both the open-drawer nudge (TopNavBase) and the guarded-send nudge
// (CopilotKitAgentContext) so either can detect it's already been shown and
// avoid appending a duplicate.
export const UPGRADE_NUDGE_MESSAGE_ID = 'upgrade-nudge-message';

/** True when the upgrade nudge has already been appended to this conversation. */
export const hasUpgradeNudgeMessage = (messages: Message[]): boolean =>
  messages.some(message => message.id === UPGRADE_NUDGE_MESSAGE_ID);

export const buildUpgradeNudgeMessage = (content: string): Message => ({
  id: UPGRADE_NUDGE_MESSAGE_ID,
  role: 'assistant',
  content,
});

type NudgeableAgent = {
  messages?: Message[];
  addMessage: (message: Message) => void;
};

/**
 * Appends the upgrade nudge to `agent` unless it's already present.
 *
 * Always dedupes against `agent.messages` — the imperative store both
 * addMessage() and this check read/write — rather than any React-state copy
 * of messages, which can lag a render behind agent.messages (see
 * useCopilotKitAgent.ts). Checking a stale copy here would let both the
 * open-drawer nudge (TopNavBase) and the guarded-send nudge
 * (CopilotKitAgentContext) each think the other hasn't shown it yet.
 *
 * Appends via agent.addMessage rather than setMessages([...messages, ...]) —
 * setMessages structuredClone()s the ENTIRE replacement array (see
 * AbstractAgent.setMessages in @ag-ui/client), which throws a DataCloneError
 * once the conversation contains a tool-call message carrying a
 * non-cloneable render closure (attached by CopilotKit for
 * frontend-rendered tools, e.g. navigate_to_page). addMessage only pushes
 * the new entry, never touching prior messages.
 */
export const appendUpgradeNudgeMessage = (
  agent: NudgeableAgent | undefined,
  content: string
): void => {
  if (!agent) return;
  if (hasUpgradeNudgeMessage(agent.messages ?? [])) return;
  agent.addMessage(buildUpgradeNudgeMessage(content));
};

/**
 * Builds a Map from toolCallId → function name by scanning assistant messages.
 * Used in multiple hooks that need to resolve tool results back to their tool name.
 */
export const buildToolCallIdToNameMap = (
  messages: Message[]
): Map<string, string> => {
  const idToName = new Map<string, string>();
  for (const msg of messages) {
    if (msg.role === 'assistant' && 'toolCalls' in msg && msg.toolCalls) {
      for (const tc of msg.toolCalls) {
        if (tc.id) idToName.set(tc.id, tc.function.name);
      }
    }
  }
  return idToName;
};
